/**
 * CRM 业务字段的简中标签 — admin /crm/* 页面共用。
 *
 * 单一来源：所有状态 / scene / 风控层 / 语言 / 动作 …的中文显示都从这里取。
 * 加新值时不要在各 Page 里散写硬编码。
 */

export const DRAFT_STATUS_LABEL: Record<string, string> = {
  generated: '已生成',
  'manual-review': '需人工审核',
  skipped: '空白稿',
};

/**
 * 待处理队列里草稿的「动作项」标签：status 本身只是 AI 起草那刻的风控结果（永不变），
 * 在 admin 待办列表里读成「已生成」会被误当成『已发』。这里按队列语境改成更准的待办措辞。
 */
export const DRAFT_QUEUE_LABEL: Record<string, string> = {
  generated: '待发送',
  'manual-review': '待人工审核',
  skipped: '待写回复',
};

export const TRIGGERED_BY_LABEL: Record<string, string> = {
  L1: 'VIP 客户',
  L2: '含敏感词或太长',
  L3: 'AI 判断有风险',
};

export const SCENE_LABEL: Record<string, string> = {
  quote: '询价',
  spec_request: '规格问询',
  sample_request: '样品请求',
  objection: '投诉/异议',
  'new-inquiry': '新询盘',
  'price-followup': '价格跟进',
  'general-followup': '一般跟进',
  chitchat: '闲聊',
  lost: '可能流失',
};

export const DRAFT_LANGUAGE_LABEL: Record<string, string> = {
  en: '英文',
  'en-us': '英文（美）',
  'en-gb': '英文（英）',
  'zh-cn': '简体中文',
  'zh-tw': '繁体中文',
  zh: '中文',
  fr: '法文',
  es: '西班牙文',
  de: '德文',
  ja: '日文',
  ko: '韩文',
  pt: '葡萄牙文',
  it: '意大利文',
  ru: '俄文',
  ar: '阿拉伯文',
  vi: '越南文',
  th: '泰文',
};

export const BUSINESS_ACTION_LABEL: Record<string, string> = {
  send: '已发送',
  edit: '改写后发送',
  transfer: '已转交',
  discard: '已丢弃',
  claim: '已接手',
  superseded: '已自动关闭',
};

export const INTENT_LABEL: Record<string, string> = {
  quote: '报价意向',
  'new-inquiry': '新询盘',
  'price-followup': '价格跟进',
  'sample-request': '样品申请',
  sample_request: '样品申请',
  objection: '客户异议',
  chitchat: '日常沟通',
  spam: '垃圾信息',
  irrelevant: '无关消息',
  spec_request: '规格问询',
  lost: '可能流失',
};

export const DECISION_TYPE_LABEL: Record<string, string> = {
  'c7.intent.classify': '意图识别',
  'f2.thread.classify': '会话分类',
  'f3.reply.draft': '回复稿',
  'c7.sla.escalate': '超时提醒',
  'c7.priority.judge': '判断优先级',
};

export function draftStatusLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return DRAFT_STATUS_LABEL[s] ?? s;
}

/** 待处理队列里草稿的待办措辞（generated→待发送…）；落不到再回落到原 status 标签。 */
export function draftQueueLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return DRAFT_QUEUE_LABEL[s] ?? DRAFT_STATUS_LABEL[s] ?? s;
}

export function triggeredByLabel(s: string | null | undefined): string {
  if (!s) return '';
  return TRIGGERED_BY_LABEL[s] ?? s;
}

export function sceneLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return SCENE_LABEL[s] ?? s;
}

export function draftLanguageLabel(s: string | null | undefined): string {
  if (!s) return '';
  return DRAFT_LANGUAGE_LABEL[s.toLowerCase()] ?? s;
}

export function businessActionLabel(s: string | null | undefined): string {
  if (!s) return '';
  return BUSINESS_ACTION_LABEL[s] ?? s;
}

export function intentLabel(s: string | null | undefined): string {
  if (!s) return '';
  return INTENT_LABEL[s] ?? s;
}

export function decisionTypeLabel(s: string | null | undefined): string {
  if (!s) return '';
  return DECISION_TYPE_LABEL[s] ?? s;
}

/* ── 货盘：站上看不看得见 ────────────────────────────────────────── */

/**
 * 产品在 CRM 里的状态。界面上只说人话 —— 不出现 active / archived 这些词。
 *
 * 「已下架」而不是「已归档」：业务员理解的是「从站上拿下来」，
 * archive 是我们库里的说法，不该漏到界面上（09-14 单子第 1 节文案那条）。
 */
export const PRODUCT_STATUS_LABEL: Record<string, string> = {
  active: '在售',
  inactive: '停用',
  draft: '草稿',
  archived: '已下架',
};

/**
 * 在售、但货盘站上看不见，是因为什么。
 *
 * 值来自 `catalog_items.hidden_reason`（判据在 `buildCatalogItem`，这里只负责翻译）。
 * **少一个值就等于界面上出现一串英文**，所以有用例逐个钉住。
 */
export const CATALOG_HIDDEN_REASON_LABEL: Record<string, string> = {
  archived: '已下架',
  off_scope: '不在本站品类',
  needs_rename: '英文名待改',
  no_image: '没有图',
  no_price: '没有价格',
};

export function productStatusLabel(s: string | null | undefined): string {
  if (!s) return '—';
  return PRODUCT_STATUS_LABEL[s] ?? s;
}

export function catalogHiddenReasonLabel(r: string | null | undefined): string {
  if (!r) return '';
  return CATALOG_HIDDEN_REASON_LABEL[r] ?? r;
}

/**
 * 站上排序权重的默认值。跟投影里的 `CATALOG_WEIGHT_DEFAULT` 同一个数。
 *
 * admin 这侧是展示层，只负责把数字翻成人话；**判据在 `buildCatalogItem`**，
 * 不要在前端再判一次「这条货该是几」—— 那就成了第二份判据。
 */
export const CATALOG_WEIGHT_DEFAULT = 10;

/**
 * 排序权重写成一句人话。数大的排前面，默认 10。
 *
 * 不把 `sort_weight` 这个词露给业务员（`feedback_ui_zh_low_friction`）：
 * 他们要知道的是「这条货在站上排前面还是后面」，不是字段叫什么。
 */
export function catalogWeightLabel(w: number | null | undefined, manual = false): string {
  if (typeof w !== 'number' || !Number.isFinite(w)) return '';
  const tail = manual ? '·人工' : '';
  if (w === CATALOG_WEIGHT_DEFAULT) return `权重 ${w}${tail}`;
  return w > CATALOG_WEIGHT_DEFAULT ? `权重 ${w}·靠前${tail}` : `权重 ${w}·靠后${tail}`;
}

/** 人工调排序时能选的档位。`value: null` = 清空人工值，回到自动判断 */
export interface CatalogWeightChoice {
  value: number | null;
  label: string;
  hint: string;
}

/**
 * 只给五档，不给业务员一个数字输入框。
 *
 * 数字框的问题不是难用，是**没人知道该填几**：填 11 和填 12 有什么区别？
 * 五档说的是意图（最前 / 靠前 / 默认 / 靠后 / 最后），值只是我们把意图翻成了数字。
 * 中间留着 2–4、6–8、21–29 的空档，以后要加档不用动已经设过的货。
 *
 * 靠后那两档特意跨过规则值 9（材质清单品名）：业务员说「往后放」时，
 * 期望的是比那批自动降权的还靠后，不是跟它们并列。
 */
export const CATALOG_WEIGHT_CHOICES: readonly CatalogWeightChoice[] = [
  { value: 30, label: '排最前面', hint: '排在所有货前面' },
  { value: 20, label: '往前排', hint: '排在普通货前面' },
  {
    value: null,
    label: '按默认',
    hint: '取消手动设置，系统自动排',
  },
  { value: 5, label: '往后排', hint: '排在普通货后面' },
  { value: 1, label: '排到最后', hint: '照常能卖能搜，只是排最后' },
] as const;

/** 一条货在某个站上的状态，写成一行给人看 */
export function catalogStateLine(
  status: string | null | undefined,
  site:
    | { slug: string; visible: boolean; hidden_reason: string | null; sort_weight?: number }
    | null
    | undefined,
  /** 这条权重是人工定的吗（`products.manual_sort_weight` 非空）。只影响措辞 */
  manualWeight = false,
): string {
  if (status === 'archived') return '已下架';
  if (!site) return `${productStatusLabel(status)} · 还没同步到站上`;
  if (site.visible) {
    /*
     * 权重只在可见时接在后面，而且**只有不等于默认值才说** ——
     * 每条货都挂一句「权重 10」是噪音，业务员会直接学会忽略这一行。
     */
    /*
     * 人工定过就**一定要说**，哪怕值正好等于默认 10：那是有人做过决定，
     * 跟「没人管过、碰巧是 10」是两回事，业务员据此知道该不该去动它。
     */
    const w =
      typeof site.sort_weight === 'number' &&
      (manualWeight || site.sort_weight !== CATALOG_WEIGHT_DEFAULT)
        ? ` · ${catalogWeightLabel(site.sort_weight, manualWeight)}`
        : '';
    return `${productStatusLabel(status)} · ${site.slug} 可见${w}`;
  }
  return `${productStatusLabel(status)} · ${site.slug} 不可见：${catalogHiddenReasonLabel(site.hidden_reason)}`;
}
